import { addDays, getDay, set, startOfDay, subDays } from 'date-fns';

import { Days, HoursOfOperations } from '@barboxx/types';

// Generated by Copilot

export interface BusinessDayWindowOptions {
    openHour?: number;
    openMinute?: number;
    closeHour?: number;
    closeMinute?: number;
    hoursOfOperations?: HoursOfOperations;
}

const DEFAULT_BUSINESS_DAY_WINDOW = {
    openHour: 10,
    openMinute: 0,
    closeHour: 3,
    closeMinute: 0,
};

const resolveWindowDefaults = (
    options?: BusinessDayWindowOptions
): Omit<Required<BusinessDayWindowOptions>, 'hoursOfOperations'> => ({
    ...DEFAULT_BUSINESS_DAY_WINDOW,
    ...options,
});

const DAYS_BY_INDEX: Days[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];

const parseHourMinute = (time: string) => {
    const [hourString = '0', minuteString = '0'] = time.split(':');
    const parsedHour = Number.parseInt(hourString, 10);
    const parsedMinute = Number.parseInt(minuteString, 10);

    return {
        hour: Number.isFinite(parsedHour) ? parsedHour : 0,
        minute: Number.isFinite(parsedMinute) ? parsedMinute : 0,
    };
};

const atTime = (date: Date, hour: number, minute: number) =>
    set(startOfDay(date), {
        hours: hour,
        minutes: minute,
        seconds: 0,
        milliseconds: 0,
    });

const resolveWindowForDate = (
    businessDayDate: Date,
    options?: BusinessDayWindowOptions
) => {
    const defaults = resolveWindowDefaults(options);
    const dayKey = DAYS_BY_INDEX[getDay(businessDayDate)];
    const dayHours = options?.hoursOfOperations?.[dayKey];

    if (!dayHours) {
        return defaults;
    }

    const open = parseHourMinute(dayHours.open);
    const close = parseHourMinute(dayHours.close);

    return {
        openHour: open.hour,
        openMinute: open.minute,
        closeHour: close.hour,
        closeMinute: close.minute,
    };
};

export const getBusinessDayRange = (
    businessDayDate: Date,
    options?: BusinessDayWindowOptions
) => {
    const { openHour, openMinute, closeHour, closeMinute } =
        resolveWindowForDate(businessDayDate, options);

    const startDateTime = atTime(businessDayDate, openHour, openMinute);
    const closesNextDay =
        closeHour < openHour ||
        (closeHour === openHour && closeMinute <= openMinute);

    const endDateBase = atTime(businessDayDate, closeHour, closeMinute);
    const endDateTime = closesNextDay ? addDays(endDateBase, 1) : endDateBase;

    return {
        startDateTime,
        endDateTime,
    };
};

export const getCurrentBusinessDayDate = (
    now: Date = new Date(),
    options?: BusinessDayWindowOptions
) => {
    const today = startOfDay(now);
    const yesterday = subDays(today, 1);

    const todayRange = getBusinessDayRange(today, options);
    if (now >= todayRange.startDateTime) {
        return today;
    }

    const yesterdayRange = getBusinessDayRange(yesterday, options);
    if (now <= yesterdayRange.endDateTime) {
        return yesterday;
    }

    return today;
};

export const getBusinessDayQueryRange = (
    startedAt?: Date,
    endedAt?: Date,
    options?: BusinessDayWindowOptions
) => {
    const startDateTime = startedAt
        ? getBusinessDayRange(startedAt, options).startDateTime.toJSON()
        : undefined;

    const endDateTime = endedAt
        ? getBusinessDayRange(endedAt, options).endDateTime.toJSON()
        : undefined;

    return {
        startDateTime,
        endDateTime,
    };
};
